import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, csrfHeaders, refreshSession, unwrap } from "@/shared/api/client";
import { sessionFrom, sessionStore, type Session } from "./session";

export type AuthStatus = "checking" | "signed-in" | "signed-out";

export interface AuthContextValue {
  status: AuthStatus;
  session: Session | null;
  signIn(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
  adopt(token: { accessToken: string; expiresAt: string; accountId: string; role: string }): void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(sessionStore.get());
  const [checking, setChecking] = useState(true);

  useEffect(() => sessionStore.subscribe(setSession), []);

  // A returning visitor with a refresh cookie is signed back in silently.
  useEffect(() => {
    let live = true;
    void refreshSession().finally(() => live && setChecking(false));
    return () => { live = false; };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const token = unwrap(await api.POST("/api/v1/auth/sign-in", { body: { email, password } }));
    sessionStore.set(sessionFrom(token));
  }, []);

  const signOut = useCallback(async () => {
    await api.POST("/api/v1/auth/sign-out", { headers: csrfHeaders() }).catch(() => undefined);
    sessionStore.set(null);
    queryClient.clear();
  }, [queryClient]);

  const adopt = useCallback((token: Parameters<AuthContextValue["adopt"]>[0]) => sessionStore.set(sessionFrom(token)), []);

  const value = useMemo<AuthContextValue>(() => ({
    status: session ? "signed-in" : checking ? "checking" : "signed-out",
    session, signIn, signOut, adopt,
  }), [session, checking, signIn, signOut, adopt]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
